/* Novora House · comportamiento del tema. JS vanilla, sin dependencias. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const theme = window.theme || {};
  const routes = theme.routes || { cartAdd: '/cart/add', cartChange: '/cart/change', root: '/' };
  const strings = theme.strings || {};

  /* Movimiento global: entradas al hacer scroll y salida suave entre páginas internas */
  const revealSelector = [
    '#MainContent > .shopify-section',
    '#MainContent .page-head',
    '#MainContent .section-head',
    '#MainContent .statement__inner',
    '#MainContent .rooms > li',
    '#MainContent .carousel__track > li',
    '#MainContent .product-grid > li',
    '#MainContent .collections-grid > li',
    '#MainContent .product-gallery',
    '#MainContent .product__info',
    '#MainContent .look__stage',
    '#MainContent .look__aside',
    '#MainContent .feature__media',
    '#MainContent .feature__body',
    '#MainContent .newsletter > *',
    '#MainContent .cart-item',
    '.site-footer__grid > *',
    '.site-footer__word',
  ].join(',');
  let revealObserver;

  function revealCandidates(scope = document) {
    const candidates = [];
    if (scope instanceof Element && scope.matches(revealSelector)) candidates.push(scope);
    candidates.push(...scope.querySelectorAll(revealSelector));
    return [...new Set(candidates)].filter((element) => {
      if (element.closest('[data-motion="none"]')) return false;
      if (element.matches('.section-hero') || element.querySelector(':scope > hero-slideshow')) return false;
      return !element.classList.contains('motion-reveal');
    });
  }

  function prepareScrollMotion(scope = document) {
    if (reduceMotion.matches || !('IntersectionObserver' in window)) return;
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          revealObserver.unobserve(entry.target);
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0 });
    }

    const orderByParent = new Map();
    revealCandidates(scope).forEach((element) => {
      const parent = element.parentElement;
      const order = Math.min(orderByParent.get(parent) || 0, 3);
      orderByParent.set(parent, order + 1);
      element.style.setProperty('--reveal-delay', `${order * 55}ms`);
      element.classList.add('motion-reveal');
      revealObserver.observe(element);
    });
  }

  function revealEverything() {
    revealObserver?.disconnect();
    revealObserver = null;
    document.querySelectorAll('.motion-reveal').forEach((element) => element.classList.add('is-revealed'));
  }

  function internalNavigationLink(event) {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
    if (!(event.target instanceof Element)) return null;
    const link = event.target.closest('a[href]');
    if (!link || link.matches('[download], [target]:not([target="_self"]), [data-drawer-open], [data-no-transition], .skip-link')) return null;
    const destination = new URL(link.href, window.location.href);
    if (!['http:', 'https:'].includes(destination.protocol) || destination.origin !== window.location.origin) return null;
    const current = new URL(window.location.href);
    if (destination.pathname === current.pathname && destination.search === current.search && destination.hash) return null;
    return link;
  }

  let isNavigating = false;
  document.addEventListener('click', (event) => {
    const link = internalNavigationLink(event);
    if (!link || reduceMotion.matches || isNavigating) return;
    event.preventDefault();
    isNavigating = true;
    document.body.classList.add('is-page-leaving');
    window.setTimeout(() => window.location.assign(link.href), 180);
  });
  window.addEventListener('pageshow', () => {
    isNavigating = false;
    document.body.classList.remove('is-page-leaving');
  });
  document.addEventListener('shopify:section:load', (event) => prepareScrollMotion(event.target));
  reduceMotion.addEventListener?.('change', (event) => {
    if (event.matches) revealEverything();
    else prepareScrollMotion();
  });

  /* Dinero con el formato de la tienda */
  function formatMoney(cents) {
    const format = theme.moneyFormat || '${{amount}}';
    const value = Number(cents || 0) / 100;
    const group = (decimals, thousands = ',', point = '.') => {
      const [int, frac] = value.toFixed(decimals).split('.');
      return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? point + frac : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return group(0);
        case 'amount_with_comma_separator': return group(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return group(0, '.', ',');
        default: return group(2);
      }
    });
  }

  /* Drawers con dialog nativo: foco atrapado y Escape gratis */
  function openDrawer(dialog, opener) {
    if (!dialog || dialog.open) return;
    dialog._opener = opener || document.activeElement;
    dialog.showModal();
    document.body.classList.add('is-locked');
  }
  function closeDrawer(dialog) {
    if (dialog && dialog.open) dialog.close();
  }
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const dialog = document.getElementById(opener.dataset.drawerOpen);
      if (dialog) { event.preventDefault(); openDrawer(dialog, opener); }
      return;
    }
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) { closeDrawer(closer.closest('dialog')); return; }
    if (event.target.matches('dialog.drawer')) closeDrawer(event.target);
  });
  /* close no burbujea: se escucha en captura para sobrevivir a recargas de sección del editor */
  document.addEventListener('close', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement) || !dialog.classList.contains('drawer')) return;
    document.body.classList.remove('is-locked');
    if (dialog._opener && document.contains(dialog._opener)) dialog._opener.focus();
  }, true);

  /* Carrito: render de secciones */
  function replaceSection(html, selector) {
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    const current = document.querySelector(selector);
    if (next && current) current.replaceWith(next);
    return next;
  }
  function syncCount() {
    const source = document.querySelector('[data-cart-count-value]');
    if (!source) return;
    const count = Number(source.dataset.cartCountValue || 0);
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.hidden = count === 0;
    });
    document.querySelectorAll('[data-cart-label]').forEach((el) => {
      el.setAttribute('aria-label', (strings.cartLabel || 'Carrito') + ': ' + count);
    });
  }

  /* Secciones del carrito presentes en la página: el cajón y, en /cart, también la página */
  function cartSectionTargets() {
    const targets = [];
    if (document.getElementById('CartDrawerContents')) targets.push({ id: 'cart-drawer', target: 'CartDrawerContents' });
    document.querySelectorAll('cart-items[data-section-id]').forEach((el) => {
      if (el.dataset.sectionId !== 'cart-drawer') targets.push({ id: el.dataset.sectionId, target: el.dataset.target });
    });
    return targets;
  }
  function applyCartSections(sections, targets) {
    if (sections) targets.forEach(({ id, target }) => { if (sections[id]) replaceSection(sections[id], '#' + target); });
    syncCount();
  }

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.form && this.form.querySelector('[type="submit"]');
      this.error = this.querySelector('[data-form-error]');
      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }
    async onSubmit(event) {
      const drawer = document.getElementById('CartDrawer');
      if (!drawer || !window.fetch || theme.cartType === 'page') return;
      event.preventDefault();
      if (this.button.getAttribute('aria-busy') === 'true') return;
      const label = this.button.querySelector('span') || this.button;
      const idle = label.textContent;
      this.button.setAttribute('aria-busy', 'true');
      label.textContent = strings.adding || 'Agregando';
      if (this.error) this.error.hidden = true;
      try {
        const targets = cartSectionTargets();
        const body = new FormData(this.form);
        body.append('sections', targets.map((t) => t.id).join(','));
        body.append('sections_url', window.location.pathname);
        const response = await fetch(`${routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const data = await response.json();
        if (data.status) throw new Error(data.description || data.message);
        applyCartSections(data.sections, targets);
        openDrawer(drawer, this.button);
      } catch (error) {
        if (this.error) {
          this.error.querySelector('[data-form-error-text]').textContent = error.message || strings.cartError;
          this.error.hidden = false;
        }
      } finally {
        this.button.removeAttribute('aria-busy');
        label.textContent = idle;
      }
    }
  }
  customElements.define('product-form', ProductForm);

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        const input = event.target.closest('input[data-line]');
        if (input) this.update(input.dataset.line, Math.max(0, parseInt(input.value, 10) || 0));
      });
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-remove-line]');
        if (remove) { event.preventDefault(); this.update(remove.dataset.removeLine, 0); return; }
        const step = event.target.closest('[data-qty-step]');
        if (step) {
          const input = step.parentElement.querySelector('input');
          input.value = Math.max(0, (parseInt(input.value, 10) || 0) + Number(step.dataset.qtyStep));
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
    async update(line, quantity) {
      const targets = cartSectionTargets();
      if (!targets.some((t) => t.id === this.dataset.sectionId)) targets.push({ id: this.dataset.sectionId, target: this.dataset.target });
      this.classList.add('is-updating');
      try {
        const response = await fetch(`${routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(line), quantity, sections: targets.map((t) => t.id), sections_url: window.location.pathname }),
        });
        const data = await response.json();
        if (data.status) throw new Error(data.description || data.message);
        applyCartSections(data.sections, targets);
      } catch (error) {
        this.classList.remove('is-updating');
        const status = this.querySelector('[data-cart-error]');
        if (status) { status.textContent = error.message || strings.cartError; status.hidden = false; }
      }
    }
  }
  customElements.define('cart-items', CartItems);

  /* Hero */
  class HeroSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.hero__slide')];
      this.tabs = [...this.querySelectorAll('.hero__tab')];
      this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
      if (this.slides.length < 2) return;
      this.querySelector('[data-hero-prev]')?.addEventListener('click', () => this.go(this.index - 1, true));
      this.querySelector('[data-hero-next]')?.addEventListener('click', () => this.go(this.index + 1, true));
      this.tabs.forEach((tab, i) => tab.addEventListener('click', () => this.go(i, true)));
      this.querySelector('.hero__tabs')?.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        this.go(this.index + (event.key === 'ArrowRight' ? 1 : -1), true);
        this.tabs[this.index].focus();
      });
      this.delay = Number(this.dataset.autoplay || 0) * 1000;
      this.toggle = this.querySelector('[data-hero-toggle]');
      if (this.delay && !reduceMotion.matches) {
        this.playing = true;
        this.toggle?.addEventListener('click', () => this.setPlaying(!this.playing));
        this.addEventListener('focusin', () => this.stop());
        this.addEventListener('focusout', () => this.playing && this.start());
        this.addEventListener('pointerenter', () => this.stop());
        this.addEventListener('pointerleave', () => this.playing && this.start());
        this.start();
      } else if (this.toggle) {
        this.toggle.hidden = true;
      }
    }
    disconnectedCallback() { this.stop(); }
    setPlaying(playing) {
      this.playing = playing;
      this.toggle.setAttribute('aria-pressed', String(!playing));
      this.toggle.setAttribute('aria-label', playing ? strings.pause : strings.play);
      this.toggle.querySelector('[data-icon-pause]').hidden = !playing;
      this.toggle.querySelector('[data-icon-play]').hidden = playing;
      if (playing) this.start(); else this.stop();
    }
    start() { this.stop(); this.timer = window.setInterval(() => this.go(this.index + 1), this.delay); }
    stop() { window.clearInterval(this.timer); }
    go(next, fromUser) {
      const count = this.slides.length;
      this.index = (next + count) % count;
      this.slides.forEach((slide, i) => {
        const active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
      });
      this.tabs.forEach((tab, i) => {
        const active = i === this.index;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      if (fromUser && this.playing) this.start();
    }
  }
  customElements.define('hero-slideshow', HeroSlideshow);

  /* Carrusel de producto */
  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.carousel__track');
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      if (!this.track || !this.prev || !this.next) return;
      const step = () => {
        const item = this.track.firstElementChild;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return item ? item.getBoundingClientRect().width + gap : this.track.clientWidth;
      };
      const move = (dir) => this.track.scrollBy({ left: dir * step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
      this.prev.addEventListener('click', () => move(-1));
      this.next.addEventListener('click', () => move(1));
      const first = this.track.firstElementChild;
      const last = this.track.lastElementChild;
      if (!first || !('IntersectionObserver' in window)) return;
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const atEdge = entry.intersectionRatio > 0.9;
          if (entry.target === first) this.prev.disabled = atEdge;
          if (entry.target === last) this.next.disabled = atEdge;
        });
      }, { root: this.track, threshold: [0, 0.9, 1] });
      this.observer.observe(first);
      if (last !== first) this.observer.observe(last);
    }
    disconnectedCallback() { this.observer?.disconnect(); }
  }
  customElements.define('product-carousel', ProductCarousel);

  /* Galería del producto: una imagen de variante seguida de los medios compartidos */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.allSlides = [...this.querySelectorAll('[data-gallery-slide]')];
      this.thumbnails = this.querySelector('[data-gallery-thumbnails]');
      this.allThumbnails = [...this.querySelectorAll('[data-gallery-thumbnail]')];
      this.prev = this.querySelector('[data-gallery-prev]');
      this.next = this.querySelector('[data-gallery-next]');
      this.controls = this.querySelector('.product-gallery__controls');
      this.current = this.querySelector('[data-gallery-current]');
      this.total = this.querySelector('[data-gallery-total]');
      this.product = this.closest('.product');

      this.onPrevious = () => this.go(this.index - 1);
      this.onNext = () => this.go(this.index + 1);
      this.onKeydown = (event) => {
        const keys = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.visibleSlides.length - 1 };
        if (!(event.key in keys)) return;
        event.preventDefault();
        this.go(keys[event.key]);
      };
      this.onThumbnailClick = (event) => {
        const thumbnail = event.target.closest('[data-gallery-thumbnail]');
        if (!thumbnail || !this.thumbnails.contains(thumbnail)) return;
        const index = this.visibleSlides.findIndex((slide) => slide.dataset.mediaId === thumbnail.dataset.mediaId);
        if (index !== -1) this.go(index);
      };
      this.onScroll = () => {
        window.cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = window.requestAnimationFrame(() => this.syncFromScroll());
      };
      this.onVariantChange = (event) => this.setVariant(event.detail?.variant || null, true);

      this.prev?.addEventListener('click', this.onPrevious);
      this.next?.addEventListener('click', this.onNext);
      this.track.addEventListener('keydown', this.onKeydown);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.thumbnails?.addEventListener('click', this.onThumbnailClick);
      this.product?.addEventListener('variant:change', this.onVariantChange);

      const mediaId = this.dataset.selectedMediaId;
      const variantId = this.dataset.selectedVariantId;
      this.setVariant({ id: variantId, featured_media: mediaId ? { id: mediaId } : null });
      this.classList.add('is-ready');
    }
    disconnectedCallback() {
      this.prev?.removeEventListener('click', this.onPrevious);
      this.next?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('keydown', this.onKeydown);
      this.track?.removeEventListener('scroll', this.onScroll);
      this.thumbnails?.removeEventListener('click', this.onThumbnailClick);
      this.product?.removeEventListener('variant:change', this.onVariantChange);
      window.cancelAnimationFrame(this.scrollFrame);
      window.clearTimeout(this.variantAnimationTimer);
    }
    variantIds(element) {
      return (element.dataset.variantIds || '').split(',').map((id) => id.trim()).filter(Boolean);
    }
    setVariant(variant, animate = false) {
      const variantId = String(variant?.id || '');
      const mediaId = String(variant?.featured_media?.id || variant?.featured_image?.id || '');
      const primary = this.allSlides.find((slide) => slide.dataset.mediaId === mediaId)
        || this.allSlides.find((slide) => variantId && this.variantIds(slide).includes(variantId));
      const shared = this.allSlides.filter((slide) => this.variantIds(slide).length === 0);
      let visible = primary ? [primary, ...shared.filter((slide) => slide !== primary)] : shared;
      if (!visible.length) visible = [...this.allSlides];

      const visibleIds = new Set(visible.map((slide) => slide.dataset.mediaId));
      const hiddenSlides = this.allSlides.filter((slide) => !visibleIds.has(slide.dataset.mediaId));
      visible.forEach((slide, index) => {
        slide.hidden = false;
        slide.style.setProperty('--gallery-order', index);
      });
      hiddenSlides.forEach((slide, index) => {
        slide.hidden = true;
        slide.style.setProperty('--gallery-order', visible.length + index);
      });
      this.track.append(...visible, ...hiddenSlides);

      const visibleThumbnails = visible
        .map((slide) => this.allThumbnails.find((thumbnail) => thumbnail.dataset.mediaId === slide.dataset.mediaId))
        .filter(Boolean);
      const hiddenThumbnails = this.allThumbnails.filter((thumbnail) => !visibleIds.has(thumbnail.dataset.mediaId));
      visibleThumbnails.forEach((thumbnail, index) => {
        thumbnail.hidden = false;
        thumbnail.style.setProperty('--gallery-order', index);
      });
      hiddenThumbnails.forEach((thumbnail, index) => {
        thumbnail.hidden = true;
        thumbnail.style.setProperty('--gallery-order', visible.length + index);
      });
      this.thumbnails?.append(...visibleThumbnails, ...hiddenThumbnails);

      this.visibleSlides = visible;
      this.visibleThumbnails = visibleThumbnails;
      this.track.scrollLeft = 0;
      if (this.thumbnails) this.thumbnails.scrollLeft = 0;
      this.updateActive(0);
      if (animate) this.animateVariantMedia(visible[0]);
    }
    animateVariantMedia(slide) {
      this.animatedSlide?.classList.remove('is-variant-entering');
      window.clearTimeout(this.variantAnimationTimer);
      this.animatedSlide = null;
      if (!slide || reduceMotion.matches) return;

      // Fuerza un nuevo ciclo de animación incluso si el cliente cambia de opción rápidamente.
      slide.classList.remove('is-variant-entering');
      void slide.offsetWidth;
      slide.classList.add('is-variant-entering');
      this.animatedSlide = slide;
      this.variantAnimationTimer = window.setTimeout(() => {
        slide.classList.remove('is-variant-entering');
        if (this.animatedSlide === slide) this.animatedSlide = null;
      }, 500);
    }
    slideLeft(slide) {
      return slide.getBoundingClientRect().left - this.track.getBoundingClientRect().left + this.track.scrollLeft;
    }
    go(next, smooth = true) {
      if (!this.visibleSlides.length) return;
      const index = Math.max(0, Math.min(next, this.visibleSlides.length - 1));
      this.updateActive(index);
      this.track.scrollTo({ left: this.slideLeft(this.visibleSlides[index]), behavior: smooth && !reduceMotion.matches ? 'smooth' : 'auto' });
      const thumbnail = this.visibleThumbnails[index];
      if (thumbnail && this.thumbnails) {
        const left = thumbnail.getBoundingClientRect().left - this.thumbnails.getBoundingClientRect().left + this.thumbnails.scrollLeft;
        const centered = left - (this.thumbnails.clientWidth - thumbnail.getBoundingClientRect().width) / 2;
        this.thumbnails.scrollTo({ left: Math.max(0, centered), behavior: smooth && !reduceMotion.matches ? 'smooth' : 'auto' });
      }
    }
    syncFromScroll() {
      if (!this.visibleSlides.length) return;
      const left = this.track.scrollLeft;
      let closest = 0;
      let distance = Infinity;
      this.visibleSlides.forEach((slide, index) => {
        const candidate = Math.abs(this.slideLeft(slide) - left);
        if (candidate < distance) { closest = index; distance = candidate; }
      });
      this.updateActive(closest);
    }
    pauseMedia(slide) {
      slide.querySelectorAll('video').forEach((video) => video.pause());
      slide.querySelectorAll('model-viewer').forEach((model) => model.pause?.());
      slide.querySelectorAll('iframe').forEach((frame) => {
        frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*');
        frame.contentWindow?.postMessage({ method: 'pause' }, '*');
      });
    }
    updateActive(index) {
      this.index = index;
      const count = this.visibleSlides.length;
      this.allSlides.forEach((slide) => {
        const visibleIndex = this.visibleSlides.indexOf(slide);
        const active = visibleIndex === index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', String(!active));
        slide.inert = !active;
        if (!active) this.pauseMedia(slide);
        if (visibleIndex !== -1) slide.setAttribute('aria-label', `${visibleIndex + 1} de ${count}`);
      });
      this.allThumbnails.forEach((thumbnail) => {
        const visibleIndex = this.visibleThumbnails.indexOf(thumbnail);
        const active = visibleIndex === index;
        thumbnail.classList.toggle('is-active', active);
        thumbnail.setAttribute('aria-current', String(active));
        if (visibleIndex !== -1) thumbnail.setAttribute('aria-label', `Ver imagen ${visibleIndex + 1} de ${count}`);
      });
      if (this.current) this.current.textContent = String(index + 1);
      if (this.total) this.total.textContent = String(count);
      if (this.prev) this.prev.disabled = index === 0;
      if (this.next) this.next.disabled = index === count - 1;
      if (this.controls) this.controls.hidden = count < 2;
      if (this.thumbnails) this.thumbnails.hidden = count < 2;
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* Arma el ambiente */
  class LookHotspots extends HTMLElement {
    connectedCallback() {
      this.buttons = [...this.querySelectorAll('.hotspot__button')];
      this.items = [...this.querySelectorAll('[data-look-item]')];
      this.buttons.forEach((button) => {
        button.addEventListener('click', () => this.toggle(button));
      });
      this.onOutside = (event) => { if (!this.contains(event.target)) this.closeAll(); };
      document.addEventListener('click', this.onOutside);
      this.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        const open = this.buttons.find((b) => b.getAttribute('aria-expanded') === 'true');
        if (open) { this.closeAll(); open.focus(); }
      });
    }
    disconnectedCallback() { document.removeEventListener('click', this.onOutside); }
    toggle(button) {
      const wasOpen = button.getAttribute('aria-expanded') === 'true';
      this.closeAll();
      if (wasOpen) return;
      button.setAttribute('aria-expanded', 'true');
      this.items.find((item) => item.dataset.lookItem === button.dataset.index)?.classList.add('is-highlighted');
    }
    closeAll() {
      this.buttons.forEach((b) => b.setAttribute('aria-expanded', 'false'));
      this.items.forEach((item) => item.classList.remove('is-highlighted'));
    }
  }
  customElements.define('look-hotspots', LookHotspots);

  /* Selector de variantes */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('script[type="application/json"]');
      if (!data) return;
      this.variants = JSON.parse(data.textContent);
      this.form = document.getElementById(this.dataset.form);
      this.price = document.getElementById(this.dataset.price);
      this.addEventListener('change', () => this.update());
      const selected = this.selected();
      this.markAvailability(selected);
    }
    selected() {
      return [...this.querySelectorAll('fieldset')].map((fieldset) => fieldset.querySelector('input:checked')?.value);
    }
    markAvailability(selected) {
      this.querySelectorAll('fieldset').forEach((fieldset, position) => {
        fieldset.querySelectorAll('input').forEach((input) => {
          const candidate = selected.slice();
          candidate[position] = input.value;
          const available = this.variants.some((v) => v.available && v.options.every((o, i) => o === candidate[i]));
          input.classList.toggle('is-unavailable', !available);
        });
      });
    }
    findVariant(selected) {
      return this.variants.find((variant) => variant.options.every((option, index) => option === selected[index]));
    }
    notify(variant) {
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant } }));
    }
    update() {
      const selected = this.selected();
      const variant = this.findVariant(selected);
      this.markAvailability(selected);
      if (!variant) {
        const button = this.form?.querySelector('[type="submit"]');
        if (button) {
          button.disabled = true;
          (button.querySelector('span') || button).textContent = strings.unavailable;
        }
        this.notify(null);
        return;
      }
      const button = this.form?.querySelector('[type="submit"]');
      const id = this.form?.querySelector('input[name="id"]');
      if (id) id.value = variant.id;
      if (button) {
        button.disabled = !variant.available;
        (button.querySelector('span') || button).textContent = variant.available ? strings.addToCart : strings.soldOut;
      }
      if (this.price) {
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        this.price.innerHTML = onSale
          ? `<span class="visually-hidden">${strings.salePrice}</span><span>${formatMoney(variant.price)}</span><span class="visually-hidden">${strings.regularPrice}</span><s>${formatMoney(variant.compare_at_price)}</s>`
          : `<span>${formatMoney(variant.price)}</span>`;
      }
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
      this.notify(variant);
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* Cantidad en la ficha de producto */
  document.addEventListener('click', (event) => {
    const step = event.target.closest('.qty [data-qty-step]');
    if (!step || step.closest('cart-items')) return;
    const input = step.parentElement.querySelector('input');
    const min = Number(input.min || 1);
    input.value = Math.max(min, (parseInt(input.value, 10) || min) + Number(step.dataset.qtyStep));
  });

  /* Filtros: aplican al cambiar */
  class FilterForm extends HTMLElement {
    connectedCallback() {
      const form = this.querySelector('form');
      if (!form) return;
      this.querySelector('[data-filter-submit]')?.setAttribute('hidden', '');
      form.addEventListener('change', () => {
        window.clearTimeout(this.timer);
        this.timer = window.setTimeout(() => form.requestSubmit(), 350);
      });
    }
  }
  customElements.define('filter-form', FilterForm);

  prepareScrollMotion();
})();
